import React, { memo } from 'react';
import { func, bool, arrayOf, shape, string } from 'prop-types';
import { reduxForm } from 'redux-form';
import { validateAnimatedCreditCard, required } from '@widergy/web-utils/lib/validate';
import { handleKeyDown } from '@widergy/utilitygo-forms-web';
import { scrollToInvalid } from '@widergy/web-utils/lib/form';
import { UTButton, UTLabel, UTLoading } from '@widergy/energy-ui';

import { PAYMENT_FORM } from 'constants/reduxForms';
import { creditCardErrorMessages } from 'constants/validationMessages';
import { FIELD_ANIMATED_CREDIT_CARD, FIELD_SELECT } from 'constants/userForms';
import UTForm from 'app/components/UTForm';
import { message } from 'utils/stringUtils';
import { appConfig } from 'config/customization';
import { emailField } from 'constants/fields';

import styles from './styles.module.scss';

const defaultCardTypeOptions = [
  { id: 0, key: 'default_debit', value: 'default_debit', name: message('Payments:debitCard') },
  { id: 1, key: 'default_credit', value: 'default_credit', name: message('Payments:creditCard') }
];

const cardTypeOptions = appConfig.paymentFlow.cardTypeOptions || defaultCardTypeOptions;

const creditCardChildKeys = ['cardnumber', 'holdername', 'cvm', 'expmonth', 'expyear'];

const defaultFields = [
  {
    id: '1',
    type: FIELD_SELECT,
    key: 'cardFunction',
    title: message('Payments:cardType'),
    field_options: cardTypeOptions,
    validate: [required(message('Validations:required'))]
  },
  {
    id: '2',
    type: FIELD_ANIMATED_CREDIT_CARD,
    key: 'cardNumber',
    validate: [validateAnimatedCreditCard(creditCardErrorMessages, creditCardChildKeys)],
    child_keys: creditCardChildKeys
  }
];

const PaymentForm = ({
  handleSubmit,
  onCancel,
  submitFailed,
  invalid,
  loading,
  hiddenInputs,
  formRef,
  onSubmitButtonPress,
  isPublic
}) => {
  const HiddenInputs = hiddenInputs.map(input => (
    <input key={input.name} type="hidden" name={input.name} value={input.value} />
  ));

  const fields =
    isPublic && appConfig.payments.paymentsWithEmail ? [...defaultFields, emailField] : defaultFields;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={styles.container} onKeyDown={handleKeyDown(handleSubmit)}>
      <UTLoading loading={loading} className={styles.loading}>
        <UTLabel className={styles.label}>{message('Payments:requireDataInput')}</UTLabel>
        <UTForm fields={fields} />
        <form ref={formRef} method="post" action={process.env.REACT_APP_FIRST_DATA_URL} target="_self">
          {HiddenInputs}
        </form>
        <div className={styles.actions}>
          <UTButton secondary onPress={onCancel} className={styles.button}>
            {message('Commons:cancel')}
          </UTButton>
          <UTButton
            onPress={onSubmitButtonPress}
            disabled={submitFailed && invalid}
            className={styles.button}
          >
            {message('Payments:send')}
          </UTButton>
        </div>
      </UTLoading>
    </div>
  );
};

PaymentForm.propTypes = {
  handleSubmit: func,
  onSubmitButtonPress: func,
  onCancel: func,
  submitFailed: bool,
  invalid: bool,
  loading: bool,
  formRef: shape({ current: string }),
  hiddenInputs: arrayOf(shape({ name: string.isRequired, value: string.isRequired })),
  isPublic: bool
};

PaymentForm.defaultProps = {
  hiddenInputs: []
};

export default reduxForm({ form: PAYMENT_FORM, onSubmitFail: errors => scrollToInvalid(errors) })(
  memo(PaymentForm)
);
